import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Megaphone, FileText, Link2, ArrowUpRight, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NoticesPage() {
  const [notices, setNotices] = useState(null);

  useEffect(() => {
    api.get("/notices").then((r) => setNotices(r.data)).catch(() => setNotices([]));
  }, []);

  return (
    <div data-testid="notices-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-4xl px-6 pb-28 pt-32 sm:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700"
        >
          <Megaphone size={13} /> Notice Board
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
          className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl"
        >
          Notices &amp; announcements.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="mt-5 max-w-xl text-base leading-relaxed text-slate-600"
        >
          Official notices from Jyoti Academy — batch schedules, exam alerts and
          circulars, as PDF or Google Doc.
        </motion.p>

        {notices === null ? (
          <div className="mt-16 flex items-center gap-3 text-slate-500" data-testid="notices-loading">
            <Loader2 size={18} className="animate-spin" /> Loading…
          </div>
        ) : notices.length === 0 ? (
          <p className="mt-16 rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="notices-empty">
            No notices yet — announcements from the admin will appear here.
          </p>
        ) : (
          <div className="mt-14 space-y-4">
            {notices.map((n, i) => {
              const isPdf = n.kind === "pdf";
              const href = isPdf ? `${API_BASE}/notices/file/${n.id}` : n.url;
              const Icon = isPdf ? FileText : Link2;
              return (
                <motion.a
                  key={n.id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.07, duration: 0.7, ease: EASE }}
                  whileHover={{ x: 6 }}
                  data-testid={`notice-card-${i}`}
                  className="group flex items-center gap-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-colors duration-200 hover:border-sky-300 hover:bg-sky-50/60"
                >
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white ${isPdf ? "bg-red-500" : "bg-sky-700"}`}>
                    <Icon size={20} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-lg font-bold text-slate-900">{n.title}</span>
                    {n.note && <span className="mt-0.5 block truncate text-sm text-slate-500">{n.note}</span>}
                    <span className="mt-1.5 inline-flex items-center gap-2">
                      <span className={`rounded-full px-3 py-0.5 text-[11px] font-bold uppercase tracking-widest ${isPdf ? "bg-red-50 text-red-700" : "bg-sky-100 text-sky-800"}`}>
                        {isPdf ? "PDF" : "Google Doc"}
                      </span>
                      {n.created_at && (
                        <span className="text-[11px] text-slate-400">
                          {new Date(n.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      )}
                    </span>
                  </span>
                  <ArrowUpRight size={20} className="shrink-0 text-slate-400 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-sky-700" />
                </motion.a>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
